/**
 * Agent registry list page — `GET /agents`.
 *
 * Two presentations share the same delete/clone flows:
 *   - **Card (grid) view** — marketplace-style gallery with page-owned filters + paging
 *     (the pre-migration UX users still expect on this screen).
 *   - **Table view** — `CordioAbpPagedDataTable` + `createCortexPagedDataSource` so the E2E
 *     grid checklist (GRID-01…21) keeps a stable toolbar / pagination contract.
 *
 * The table owns its own fetch; the card view owns a parallel `AgentFilters` fetch. Switching
 * views does not share filter state — each mode keeps the controls that match its layout
 * (dropdowns on cards, status tabs + filter popover on the table).
 *
 * ─── Table column plan ───────────────────────────────────────────────────────────────────────────
 * field       │ header      │ width │ cell type            │ filter
 * ────────────┼─────────────┼───────┼──────────────────────┼──────────────────────────────────────
 * name        │ Name        │  20%  │ text (clickable)     │ toolbar search (`searchTerm`)
 * slug        │ Slug        │  16%  │ text                 │ —
 * description │ Description │  26%  │ text (read-more 100) │ —
 * category    │ Category    │  16%  │ text                 │ popover select → `category`
 * isSystem    │ Type        │  10%  │ custom Chip          │ popover select → `isSystem`
 * isActive    │ Status      │  12%  │ custom Chip          │ status tabs → synthetic `status`
 *
 * No column is `sortable`: `agentService.getAgents` has no `sortBy` / `sortDescending` slot.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, ChevronLeft, ChevronRight, Info, LayoutGrid, Plus, Rows, Trash2 } from 'lucide-react';

import {
  Button,
  Chip,
  CordioAbpPagedDataTable,
  CoordioDropdown,
  FormField,
  Input,
  ShellHeader,
  type CordioDataTableAction,
  type CordioDataTableColumn,
  type CordioDataTableListToolbarConfig,
} from '@coordio/ui';
import { EmptyState, InlineErrorPanel, LoadingSkeleton } from '@coordio/ui/layout';

import { AgentCard } from '../components/agents/AgentCard';
import { createCortexPagedDataSource } from '../components/data-display/cortexPagedDataSource';
import { DeleteDialog, useDeleteDialog } from '../components/delete-dialog';
import { agentService } from '../services/agentService';
import { useContextStore } from '../store/contextStore';
import { useNotificationStore } from '../store/notificationStore';
import { ROUTES } from '../utils/routes';

import type { AgentDefinitionDto, AgentFilters } from '../types/agent';

import './AgentRegistryPage.scss';

type ViewMode = 'grid' | 'table';

const STATUS_TAB_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'true' },
  { label: 'Inactive', value: 'false' },
] as const;

const STATUS_TAB_PARAM_KEY = 'status';

const AGENT_CATEGORIES = [
  'Infrastructure Management',
  'Security & Compliance',
  'Financial Operations',
  'User Support',
  'Monitoring & Observability',
  'Automation & Orchestration',
] as const;

const CARD_PAGE_SIZE = 12;

function toAgentQuery(query: Record<string, unknown>): Record<string, unknown> {
  const { [STATUS_TAB_PARAM_KEY]: status, isSystem, ...rest } = query;

  const mapped: Record<string, unknown> = { ...rest };

  if (status === 'true' || status === 'false') {
    mapped.isActive = status === 'true';
  }
  if (isSystem === 'true' || isSystem === 'false') {
    mapped.isSystem = isSystem === 'true';
  }

  return mapped;
}

export const AgentRegistryPage = () => {
  const navigate = useNavigate();

  const currentTenant = useContextStore((s) => s.currentTenant);
  const currentProject = useContextStore((s) => s.currentProject);
  const showNotification = useNotificationStore((s) => s.showNotification);

  /** Default card gallery — matches the pre-migration Agent Registry UX. */
  const [viewMode, setViewMode] = useState<ViewMode>('grid');

  /** Bumped after a mutation so the Cordio table rebuilds its dataSource. */
  const [listVersion, setListVersion] = useState(0);

  const { dialogRef, pendingItem, openDelete, closeDelete } = useDeleteDialog<AgentDefinitionDto>();

  const canCreateAgent = Boolean(currentTenant && currentProject);

  const tenantId = currentTenant?.id;
  const projectId = currentProject?.id;

  // ─── Card view: page-owned filters + fetch ────────────────────────────────────────────────────
  const [cardFilters, setCardFilters] = useState<AgentFilters>({
    pageNumber: 1,
    pageSize: CARD_PAGE_SIZE,
    searchTerm: '',
    category: '',
    isSystem: undefined,
    isActive: true,
  });
  const [cardAgents, setCardAgents] = useState<AgentDefinitionDto[]>([]);
  const [cardLoading, setCardLoading] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const [cardTotalCount, setCardTotalCount] = useState(0);

  const loadCardAgents = useCallback(async () => {
    setCardLoading(true);
    setCardError(null);
    try {
      const result = await agentService.getAgents({
        ...cardFilters,
        category: cardFilters.category || undefined,
        searchTerm: cardFilters.searchTerm || undefined,
      });
      setCardAgents(result.items);
      setCardTotalCount(result.totalCount);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load agents';
      setCardError(message);
      // Read from the store at call time so this callback stays stable across renders
      // (a changing `showNotification` identity would re-fire the card fetch forever).
      useNotificationStore.getState().showNotification(message, 'error');
    } finally {
      setCardLoading(false);
    }
  }, [cardFilters]);

  useEffect(() => {
    if (viewMode !== 'grid') return;
    void loadCardAgents();
  }, [viewMode, loadCardAgents, listVersion, tenantId, projectId]);

  const cardTotalPages = Math.max(1, Math.ceil(cardTotalCount / (cardFilters.pageSize || CARD_PAGE_SIZE)));

  const dataSource = useMemo(
    () => createCortexPagedDataSource<AgentDefinitionDto>((query) => agentService.getAgents(toAgentQuery(query))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tenantId, projectId, listVersion],
  );

  const columns = useMemo<ReadonlyArray<CordioDataTableColumn<AgentDefinitionDto>>>(
    () => [
      { field: 'name', header: 'Name', type: 'text', width: '20%', clickable: true },
      { field: 'slug', header: 'Slug', type: 'text', width: '16%' },
      {
        field: 'description',
        header: 'Description',
        type: 'text',
        width: '26%',
        maxLength: 100,
        showReadMore: true,
      },
      { field: 'category', header: 'Category', type: 'text', width: '16%' },
      {
        field: 'isSystem',
        header: 'Type',
        type: 'custom',
        width: '10%',
        customCell: (row) => <Chip label={row.isSystem ? 'System' : 'Custom'} color={row.isSystem ? 'blue' : 'default'} />,
      },
      {
        field: 'isActive',
        header: 'Status',
        type: 'custom',
        width: '12%',
        customCell: (row) => (
          <Chip label={row.isActive ? 'Active' : 'Inactive'} color={row.isActive ? 'green' : 'default'} />
        ),
      },
    ],
    [],
  );

  const listToolbar = useMemo<CordioDataTableListToolbarConfig>(
    () => ({
      search: { placeholder: 'Search agents by name' },
      statusTabs: {
        paramKey: STATUS_TAB_PARAM_KEY,
        ariaLabel: 'Agent status filter',
        options: [...STATUS_TAB_OPTIONS],
      },
      filters: [
        {
          key: 'category',
          label: 'Category',
          type: 'select',
          options: AGENT_CATEGORIES.map((category) => ({ label: category, value: category })),
        },
        {
          key: 'isSystem',
          label: 'Type',
          type: 'select',
          options: [
            { label: 'System', value: 'true' },
            { label: 'Custom', value: 'false' },
          ],
        },
      ],
      filterButtonLabel: 'Filter',
    }),
    [],
  );

  const handleView = useCallback(
    (agent: AgentDefinitionDto) => {
      navigate(ROUTES.AGENT_DETAIL.replace(':id', agent.id));
    },
    [navigate],
  );

  const handleEdit = useCallback(
    (agent: AgentDefinitionDto) => {
      if (agent.isSystem) {
        showNotification('System agents cannot be edited', 'warning');
        return;
      }
      navigate(ROUTES.AGENT_EDIT.replace(':id', agent.id));
    },
    [navigate, showNotification],
  );

  const handleClone = useCallback(
    async (agent: AgentDefinitionDto) => {
      try {
        const cloned = await agentService.cloneAgent(agent.id);
        showNotification('Agent cloned successfully', 'success');
        navigate(ROUTES.AGENT_DETAIL.replace(':id', cloned.id));
      } catch (err) {
        showNotification(err instanceof Error ? err.message : 'Failed to clone agent', 'error');
      }
    },
    [navigate, showNotification],
  );

  const actions = useMemo<ReadonlyArray<CordioDataTableAction<AgentDefinitionDto>>>(
    () => [
      {
        icon: 'eye',
        tooltip: 'View agent',
        onClick: (row) => handleView(row),
      },
      {
        icon: 'square-pen',
        tooltip: 'Edit agent',
        visible: (row) => !row.isSystem,
        onClick: (row) => handleEdit(row),
      },
      {
        icon: 'copy',
        tooltip: 'Clone agent',
        onClick: (row) => void handleClone(row),
      },
      {
        icon: 'trash-2',
        tooltip: 'Delete agent',
        className: 'agent-registry__action--destructive',
        visible: (row) => !row.isSystem,
        onClick: (row) => openDelete(row),
      },
    ],
    [handleView, handleEdit, handleClone, openDelete],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!pendingItem) return;

    await agentService.deleteAgent(pendingItem.id);
    showNotification('Agent deleted successfully', 'success');
    setListVersion((v) => v + 1);
  }, [pendingItem, showNotification]);

  const patchCardFilters = useCallback((patch: Partial<AgentFilters>) => {
    setCardFilters((prev) => ({ ...prev, ...patch, pageNumber: patch.pageNumber ?? 1 }));
  }, []);

  return (
    <div className="agent-registry" data-testid="agent-list-page">
      <ShellHeader
        title="Agent Registry"
        description="Every agent definition in this project — its prompt, its tools and the model it runs on."
        data-testid="agent-list-header"
        actions={
          <div className="agent-registry__header-actions">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={() => navigate(ROUTES.DEVELOPER_HELP)}
            >
              <BookOpen className="h-4 w-4 shrink-0" aria-hidden />
              Developer Guides
            </Button>

            <div className="agent-registry__view-toggle" role="group" aria-label="View mode">
              <Button
                type="button"
                variant={viewMode === 'grid' ? 'default' : 'outline'}
                size="sm"
                className="agent-registry__view-btn"
                onClick={() => setViewMode('grid')}
                aria-pressed={viewMode === 'grid'}
                aria-label="Card view"
                data-testid="agent-view-cards"
              >
                <LayoutGrid className="h-4 w-4" aria-hidden />
              </Button>
              <Button
                type="button"
                variant={viewMode === 'table' ? 'default' : 'outline'}
                size="sm"
                className="agent-registry__view-btn"
                onClick={() => setViewMode('table')}
                aria-pressed={viewMode === 'table'}
                aria-label="Table view"
                data-testid="agent-view-table"
              >
                <Rows className="h-4 w-4" aria-hidden />
              </Button>
            </div>

            {canCreateAgent && (
              <Button
                type="button"
                variant="default"
                size="sm"
                className="gap-2"
                data-testid="add-agent-btn"
                onClick={() => navigate(ROUTES.AGENT_NEW)}
              >
                <Plus className="h-4 w-4 shrink-0" aria-hidden />
                Create Agent
              </Button>
            )}
          </div>
        }
      />

      {!canCreateAgent && currentTenant && (
        <div className="flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800" data-testid="agent-registry-project-notice">
          <Info size={18} className="flex-shrink-0 text-blue-500" />
          <span>Select a project (using the project switcher above) to create an agent.</span>
        </div>
      )}

      {viewMode === 'grid' ? (
        <div className="agent-registry__cards-panel" data-testid="agent-list-cards">
          <div className="agent-registry__card-filters" data-testid="agent-card-filters">
            {/* Foundation CoordioDropdown (CoordioCore Site / WsSelect) — searchable panel.
                CordioAbp Filter popovers still use ShellSelect; those are restyled globally in
                index.css to match this same look. */}
            <div className="agent-registry__filter-search">
              <Input
                type="search"
                placeholder="Search agents"
                value={cardFilters.searchTerm ?? ''}
                onChange={(e) => patchCardFilters({ searchTerm: e.target.value })}
                aria-label="Search agents"
                data-testid="agent-card-search"
                className="w-full"
              />
            </div>

            <FormField label="Category" className="agent-registry__filter-field" data-testid="agent-card-category">
              <CoordioDropdown
                options={AGENT_CATEGORIES.map((category) => ({ label: category, value: category }))}
                value={cardFilters.category || null}
                onChange={(val) => patchCardFilters({ category: val ?? '' })}
                clearable
                searchable
                size="sm"
                placeholder="All Categories"
                searchPlaceholder="Search categories…"
              />
            </FormField>

            <FormField label="Type" className="agent-registry__filter-field" data-testid="agent-card-type">
              <CoordioDropdown
                options={[
                  { label: 'System', value: 'system' },
                  { label: 'Custom', value: 'custom' },
                ]}
                value={
                  cardFilters.isSystem === undefined ? null : cardFilters.isSystem ? 'system' : 'custom'
                }
                onChange={(val) => {
                  patchCardFilters({
                    isSystem: val === 'system' ? true : val === 'custom' ? false : undefined,
                  });
                }}
                clearable
                searchable
                size="sm"
                placeholder="All Types"
                searchPlaceholder="Search type…"
              />
            </FormField>

            <FormField label="Status" className="agent-registry__filter-field" data-testid="agent-card-status">
              <CoordioDropdown
                options={[
                  { label: 'Active', value: 'active' },
                  { label: 'Inactive', value: 'inactive' },
                ]}
                value={
                  cardFilters.isActive === undefined ? null : cardFilters.isActive ? 'active' : 'inactive'
                }
                onChange={(val) => {
                  patchCardFilters({
                    isActive: val === 'active' ? true : val === 'inactive' ? false : undefined,
                  });
                }}
                clearable
                searchable
                size="sm"
                placeholder="All Status"
                searchPlaceholder="Search status…"
              />
            </FormField>
          </div>

          {cardError ? (
            <div className="agent-registry__card-error">
              <InlineErrorPanel
                message={cardError}
                action={
                  <Button type="button" variant="ghost" size="sm" onClick={() => setCardError(null)}>
                    Dismiss
                  </Button>
                }
              />
            </div>
          ) : null}

          {cardLoading ? (
            <LoadingSkeleton rows={6} data-testid="agent-cards-loading" />
          ) : cardAgents.length === 0 ? (
            <EmptyState
              title="No agents found"
              description="Try adjusting your filters or create a new agent to get started."
              action={
                canCreateAgent ? (
                  <Button type="button" variant="default" size="sm" onClick={() => navigate(ROUTES.AGENT_NEW)}>
                    Create Agent
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <>
              <div className="agent-registry__card-grid">
                {cardAgents.map((agent) => (
                  <AgentCard
                    key={agent.id}
                    agent={agent}
                    onView={handleView}
                    onEdit={handleEdit}
                    onClone={handleClone}
                    onDelete={openDelete}
                  />
                ))}
              </div>

              {cardTotalPages > 1 ? (
                <div className="agent-registry__card-pagination" data-testid="agent-card-pagination">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={(cardFilters.pageNumber ?? 1) <= 1}
                    onClick={() =>
                      patchCardFilters({ pageNumber: Math.max(1, (cardFilters.pageNumber ?? 1) - 1) })
                    }
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="h-4 w-4" aria-hidden />
                  </Button>
                  <span className="agent-registry__card-page-indicator">
                    Page {cardFilters.pageNumber ?? 1} of {cardTotalPages}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={(cardFilters.pageNumber ?? 1) >= cardTotalPages}
                    onClick={() =>
                      patchCardFilters({
                        pageNumber: Math.min(cardTotalPages, (cardFilters.pageNumber ?? 1) + 1),
                      })
                    }
                    aria-label="Next page"
                  >
                    <ChevronRight className="h-4 w-4" aria-hidden />
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : (
        <div data-testid="agent-list-grid">
          <CordioAbpPagedDataTable<AgentDefinitionDto>
            className="agent-registry__table"
            columns={columns}
            dataSource={dataSource}
            initialPageSize={25}
            maxBodyHeight="520px"
            listToolbar={listToolbar}
            actions={actions}
            onRowClick={(row) => handleView(row)}
          />
        </div>
      )}

      <DeleteDialog
        ref={dialogRef}
        title="Delete agent?"
        entityLabel={pendingItem?.name}
        dialogIcon={<Trash2 className="delete-dialog__icon" />}
        description="The agent's prompt, parameters, tool attachments and knowledge files are removed. Its past sessions are kept but lose their definition. This cannot be undone."
        confirmLabel="Delete"
        confirmVariant="destructive"
        busyLabel="Deleting…"
        onConfirm={handleDeleteConfirm}
        onCancel={closeDelete}
      />
    </div>
  );
};
